import * as moment from "moment";
import { defineComponent } from "vue";
import RemoveClientChatPacket from "../../packets/client/RemoveClientChatPacket";
import AcceptMessageClientChatPacket from "../../packets/client/AcceptClientChatPacket";
import UnremoveClientChatPacket from "../../packets/client/UnremoveClientChatPacket.1";
import ChatItem, { AbstractChatItem } from "../ChatItem";

export enum MessageType {
    NORMAL = 1,
    ADMIN = 2,
    MODERATOR = 3,

    USER_BAN = 150,
    USER_UNBAN = 151,
}

export interface ChatMessageJson extends AbstractChatItem {
    id: number;
    text: string;
    type: MessageType;
    username: string;

    owned: boolean;
    accepted?: boolean;
    removed: boolean;
}

export interface AdminChatMessageJson extends ChatMessageJson {
    chatuser_id: number;
    removable: boolean;

    siteuser?: {
        id: number;
        firstname: string;
        lastname: string;
    }
}

export default defineComponent({
    components: {
        ChatItem,
    },

    template: `
            <ChatItem
                v-if="admin"
                :class="classes"
                :kind="message.kind"
                :waiting="waiting && !message.removed"
                :author="adminAuthor"
                :date="message.date"
                :controls="adminHasControls"
                :actions="actions"

                @action="onAction"
            >
                <div v-html="message.text"></div>

                <template #controls>
                    <button v-if="waiting && !message.removed" type="button" title="Akceptuj" @click="onAccept"><i class="fa fa-check"></i></button>
                    <button v-if="message.removable" type="button" title="Usuń" @click="onRemove"><i class="fa fa-times"></i></button>
                </template>
            </ChatItem>

            <ChatItem
                v-else
                :class="classes"
                :kind="message.kind"
                :waiting="waiting"
                :author="message.username"
                :date="message.date"
            >
                <div v-html="userText"></div>
            </ChatItem>
    `,

    emits: [
        'action',
    ],

    props: {
        message: { required: true },
        user: { type: Object },
        admin: { type: Boolean },
        moderated: { type: Boolean },
    },

    data() {
        return {
            visible_actions: false,
        };
    },

    computed: {
        classes() {
            const result = [];

            result.push('ChatItem--type-' + this.message.type);

            if (this.message.removed) {
                result.push('ChatItem--removed');
            }

            return result;
        },

        adminAuthor() {
            let result = this.message.username;

            if (this.message.siteuser) {
                const r = [];

                const su = this.message.siteuser;

                if (su.id) {
                    r.push('#' + su.id);
                }

                if (su.firstname) {
                    r.push(su.firstname);
                }

                if (su.lastname) {
                    r.push(su.lastname);
                }

                if (r.length) {
                    result += ' (' + r.join(' ') + ')';
                }
            }

            return result;
        },

        adminHasControls() {
            return [MessageType.NORMAL, MessageType.MODERATOR, MessageType.ADMIN].includes(this.message.type);
        },

        userText() {
            if (this.moderated && this.message.removed) {
                return 'Komentarz odrzucony przez administratora';
            }

            return this.message.text;
        },

        actions() {
            const result = [];

            if ([MessageType.NORMAL, MessageType.MODERATOR].includes(this.message.type)) {
                result.push({
                    id: 'ban10',
                    name: 'Zablokuj na 10min'
                });

                result.push({
                    id: 'ban1h',
                    name: 'Zablokuj na 1h'
                });

                result.push({
                    id: 'ban24h',
                    name: 'Zablokuj na 24h'
                });
            }

            if (this.message.removable) {
                result.push({
                    id: 'remove',
                    name: this.message.removed ? 'Przywróć' : 'Usuń'
                });
            }

            return result;
        },

        waiting() {
            if (![MessageType.NORMAL].includes(this.message.type)) {
                return false;
            }

            if (!this.moderated) {
                return false;
            }

            if (this.admin) {
                return !this.message.accepted;
            }

            if (this.message.owned) {
                return !this.message.accepted && !this.message.removed;
            } else {
                return false;
            }
        },

        timeformatted() {
            return moment(this.message.date).format('HH:mm');
        },
    },

    methods: {
        onRemove() {
            if (this.admin && this.message.removed) {
                this.$root.sendPacket(new UnremoveClientChatPacket(this.message.id));
            } else {
                this.$root.sendPacket(new RemoveClientChatPacket(this.message.id));
            }
        },

        onAccept() {
            this.$root.sendPacket(new AcceptMessageClientChatPacket(this.message.id));
        },

        doBanUser(minutes: number) {
            const msg = this.message as AdminChatMessageJson;

            this.$root.socket.emit('user-ban', {
                userid: msg.chatuser_id,
                minutes: minutes,
                ban: true,
            });
        },

        async onAction(action: string) {
            this.visible_actions = false;

            switch (action) {
                case 'ban10':
                    this.doBanUser(10);
                    break;

                case 'ban1h':
                    this.doBanUser(60);
                    break;

                case 'ban24h':
                    this.doBanUser(60 * 24);
                    break;

                case 'remove':
                    this.onRemove();
                    break;
            }

            this.$emit('action', { action: action, message: this.message });
        },
    },
});
